import React, { createContext, useContext, useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from './components/ui/sonner';
import './App.css';
import { LanguageProvider } from './contexts/LanguageContext';

// Redirect old biomall.in brand URL patterns to the current /brands/:slug route
function RedirectToBrand() {
  const { slug } = useParams();
  return <Navigate to={`/brands/${slug}`} replace />;
}

// Redirect old biomall.in category URL patterns (e.g. /category/chemicals/358) to /category/:slug
function RedirectToCategory() {
  const { slug } = useParams();
  return <Navigate to={`/category/${slug}`} replace />;
}

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ── Always-eager (tiny, needed on every page) ────────────────────────────────
import LabAuthDialog          from './components/LabAuthDialog';
import CompleteProfilePage    from './pages/CompleteProfilePage';

export const AuthDialogContext = createContext(null);
export function useAuthDialog() { return useContext(AuthDialogContext); }

// ── Lazy-loaded pages (each becomes its own JS chunk) ────────────────────────
const AdminLogin             = lazy(() => import('./pages/AdminLogin'));
const ResetPassword          = lazy(() => import('./pages/ResetPassword'));
const Microsite              = lazy(() => import('./pages/Microsite'));
const BioMallHome            = lazy(() => import('./pages/BioMallHome'));
const HomepageV2             = lazy(() => import('./pages/HomepageV2'));
const ForDistributors        = lazy(() => import('./pages/ForDistributors'));
const ForManufacturers       = lazy(() => import('./pages/ForManufacturers'));
const ContactUs             = lazy(() => import('./pages/ContactUs'));
const AboutUs               = lazy(() => import('./pages/AboutUs'));
const TermsAndConditions     = lazy(() => import('./pages/TermsAndConditions'));
const Disclaimer             = lazy(() => import('./pages/Disclaimer'));
const PrivacyPolicy          = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfSales           = lazy(() => import('./pages/TermsOfSales'));
const ReturnPolicy           = lazy(() => import('./pages/ReturnPolicy'));
const Careers                = lazy(() => import('./pages/Careers'));
// PI request pages removed
const DistributorPage        = lazy(() => import('./pages/DistributorPage'));
const DistributorsList       = lazy(() => import('./pages/DistributorsList'));
const BrandsPage             = lazy(() => import('./pages/BrandsPage'));
const BrandPage              = lazy(() => import('./pages/BrandPage'));
const CategoryPage           = lazy(() => import('./pages/CategoryPage'));
const CategoriesPage         = lazy(() => import('./pages/CategoriesPage'));
const ProductPage            = lazy(() => import('./pages/ProductPage'));
const PricingPage            = lazy(() => import('./pages/PricingPage'));
// ProfilePage removed — /me route is no longer in use
const SettingsPage           = lazy(() => import('./pages/SettingsPage'));
const CommunityPage          = lazy(() => import('./pages/CommunityPage'));
const AdminDashboard         = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminPI                = lazy(() => import('./pages/admin/AdminPI'));
const AdminSettings          = lazy(() => import('./pages/admin/AdminSettings'));
const AdminSEO               = lazy(() => import('./pages/admin/AdminSEO'));
const AdminBrands            = lazy(() => import('./pages/admin/AdminBrands'));
const AdminSubscriptions     = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminAds               = lazy(() => import('./pages/admin/AdminAds'));
const AdminAnalytics         = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminPlans             = lazy(() => import('./pages/admin/AdminPlans'));
const AdminProducts          = lazy(() => import('./pages/admin/AdminProducts'));
const AdminSponsoredProducts = lazy(() => import('./pages/admin/AdminSponsoredProducts'));
const AdminUsers             = lazy(() => import('./pages/admin/AdminUsers'));
const AdminStaff            = lazy(() => import('./pages/admin/AdminStaff'));
const AdminPageContent       = lazy(() => import('./pages/admin/AdminPageContent'));
const AdminEnquiries         = lazy(() => import('./pages/admin/AdminEnquiries'));
const AdminDistributors      = lazy(() => import('./pages/admin/AdminDistributors'));
const AdminOutreach          = lazy(() => import('./pages/admin/AdminOutreach'));
const AdminOfflineOrders     = lazy(() => import('./pages/admin/AdminOfflineOrders'));
const AdminLeadDelivery      = lazy(() => import('./pages/admin/AdminLeadDelivery'));
const AdminSearches          = lazy(() => import('./pages/admin/AdminSearches'));
const AdminBlog               = lazy(() => import('./pages/admin/AdminBlog'));
const BlogListPage           = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage           = lazy(() => import('./pages/BlogPostPage'));
const FAQPage                = lazy(() => import('./pages/FAQPage'));
const NotFound               = lazy(() => import('./pages/NotFound'));

// ── Page-transition skeleton shown while a lazy chunk loads ──────────────────
function PageSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="h-14 bg-white border-b border-gray-100 animate-pulse" />
      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-6 space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 animate-pulse space-y-3">
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-1/3" />
                <div className="h-3 bg-gray-100 rounded w-1/5" />
              </div>
            </div>
            <div className="h-3 bg-gray-100 rounded w-full" />
            <div className="h-3 bg-gray-100 rounded w-4/5" />
          </div>
        ))}
      </div>
    </div>
  );
}

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Helper: reject empty / literal "undefined" / "null" strings
    const isValid = (v) => v && v !== 'undefined' && v !== 'null' && v.length > 5;

    // Check all three auth systems in priority order

    // 1. Lab / Buyer (OTP or Google login)
    const labToken = sessionStorage.getItem('lab_session_token');
    const labUser  = sessionStorage.getItem('biomall_lab_user');
    if (isValid(labToken) && isValid(labUser)) {
      try {
        const parsed = JSON.parse(labUser);
        const role = parsed.role || parsed.user_type || 'researcher';
        setUser({ ...parsed, _authSystem: 'lab', role, user_type: role });
        setLoading(false);
        return;
      } catch (e) {
        // Corrupt — clear it
        sessionStorage.removeItem('lab_session_token');
        sessionStorage.removeItem('biomall_lab_user');
      }
    }

    // 2. Distributor chat (OTP login)
    const distToken = sessionStorage.getItem('dist_token');
    const distUser  = sessionStorage.getItem('dist_user');
    if (isValid(distToken)) {
      try {
        const parsed = distUser ? JSON.parse(distUser) : {};
        setUser({
          ...parsed,
          role: 'distributor',
          user_type: 'distributor',
          _authSystem: 'dist_chat',
        });
      } catch (e) {
        setUser({ role: 'distributor', user_type: 'distributor', _authSystem: 'dist_chat' });
      }
      setLoading(false);
      return;
    }

    // 3. Admin / Distributor-admin (password login)
    const adminToken = sessionStorage.getItem('token');
    const adminUser  = sessionStorage.getItem('user');
    if (isValid(adminToken) && isValid(adminUser)) {
      try {
        const parsed = JSON.parse(adminUser);
        setUser({ ...parsed, _authSystem: 'admin' });
        setLoading(false);
        return;
      } catch (e) {
        // Corrupt — clear it
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      }
    }

    // No auth found
    setLoading(false);
  }, []);

  const login = (userData, token, system = 'admin') => {
    if (system === 'lab') {
      // Clear all other auth systems first — prevent cross-contamination
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('dist_token');
      sessionStorage.removeItem('dist_user');
      sessionStorage.setItem('lab_session_token', token);
      sessionStorage.setItem('biomall_lab_user', JSON.stringify(userData));
    } else {
      // Clear all other auth systems first
      sessionStorage.removeItem('lab_session_token');
      sessionStorage.removeItem('biomall_lab_user');
      sessionStorage.removeItem('dist_token');
      sessionStorage.removeItem('dist_user');
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(userData));
    }
    setUser({ ...userData, _authSystem: system });
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('lab_session_token');
    sessionStorage.removeItem('biomall_lab_user');
    sessionStorage.removeItem('dist_token');
    sessionStorage.removeItem('dist_user');
    sessionStorage.removeItem('ws_turns_cache');
    sessionStorage.removeItem('ws_back_query');
    sessionStorage.removeItem('last_search');
    sessionStorage.removeItem('sf_buyer_restore');
    setUser(null);
  };

  // Role helpers — cover all naming conventions
  const isResearcher  = () => {
    return (user?.role === 'buyer' || user?.role === 'researcher' ||
            user?.user_type === 'buyer' || user?.user_type === 'researcher') &&
           user?.role !== 'distributor' && user?.role !== 'manufacturer';
  };

  const isDistributor = () =>
    user?.role === 'distributor' ||
    user?.role === 'distributor_admin' ||
    user?.user_type === 'distributor' ||
    user?._authSystem === 'dist_chat';

  const isBrand       = () =>
    user?.role === 'manufacturer' ||
    user?.user_type === 'manufacturer';

  const isAdmin       = () =>
    user?.role === 'super_admin' ||
    user?.role === 'sub_admin' ||
    user?.is_admin === true;

  const isSuperAdmin  = () =>
    user?.role === 'super_admin';

  const isLoggedIn    = () => !!user;

  const hasLocalPro      = () =>
    user?.subscription_tier === 'local_pro' ||
    user?.subscription_tier === 'international';

  const hasInternational = () =>
    user?.subscription_tier === 'international';

  const hasBrandGlobal   = () =>
    user?.subscription_tier === 'brand_global' ||
    user?.subscription_tier === 'brand_market_leader';

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      isResearcher, isDistributor, isBrand,
      isAdmin, isSuperAdmin, isLoggedIn,
      hasLocalPro, hasInternational, hasBrandGlobal,
    }}>
      {loading ? null : children}
    </AuthContext.Provider>
  );
}

function RequireAdmin({ children }) {
  const { user, isLoggedIn, loading } = useAuth();
  if (loading) return null;
  // Redirect to homepage — don't reveal the admin login URL
  if (!isLoggedIn()) return <Navigate to="/" replace />;
  const isAdmin = user?.role === 'super_admin' ||
                  user?.role === 'sub_admin' ||
                  user?.role === 'distributor_admin' ||
                  user?.is_admin === true;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function RequireAuth({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return null;
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  return children;
}

// ─── Cart context ──────────────────────────────────────────────────────────────

export const CartContext = createContext(null);

// ─── Shared quote basket (persists across BrandPage ↔ CommunityPage) ─────────
export const QuoteBasketContext = createContext(null);
export function useQuoteBasket() { return useContext(QuoteBasketContext); }
function QuoteBasketProvider({ children }) {
  const [quoteItems, setQuoteItems] = useState(() => {
    try {
      const saved = sessionStorage.getItem('quote_basket');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const persist = (items) => {
    try { sessionStorage.setItem('quote_basket', JSON.stringify(items)); } catch {}
    return items;
  };

  const addToQuote      = p  => setQuoteItems(prev => {
    if (prev.some(q => q.id === p.id)) return prev;
    return persist([...prev, p]);
  });
  const removeFromQuote = id => setQuoteItems(prev => persist(prev.filter(q => q.id !== id)));
  const clearQuote      = () => { persist([]); setQuoteItems([]); };
  return (
    <QuoteBasketContext.Provider value={{ quoteItems, addToQuote, removeFromQuote, clearQuote }}>
      {children}
    </QuoteBasketContext.Provider>
  );
}
export function useCart() {
  return useContext(CartContext);
}

function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = sessionStorage.getItem('enquiry_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [cartMode, setCartMode] = useState('enquiry'); // 'enquiry' or 'stock'

  useEffect(() => {
    try {
      sessionStorage.setItem('enquiry_cart', JSON.stringify(items));
    } catch (e) {}
  }, [items]);

  const CART_LIMIT = 10;

  const addItem = (item) => {
    // If cart is empty set mode based on item
    if (items.length === 0) {
      setCartMode(item.is_stock ? 'stock' : 'enquiry');
    }
    // If cart has items and mode conflicts, keep existing mode
    setItems(prev => {
      const exists = prev.find(i =>
        i.id === item.id ||
        (item.catalogue_number && i.catalogue_number === item.catalogue_number)
      );
      if (exists) {
        return prev.map(i =>
          (i.id === item.id ||
           (item.catalogue_number && i.catalogue_number === item.catalogue_number))
            ? { ...i, qty: (i.qty || 1) + 1 }
            : i
        );
      }
      if (prev.length >= CART_LIMIT) return prev;
      return [...prev, {
        ...item,
        qty: item.qty || item.default_qty || 1,
        price: item.price || '',
        delivery: '',
      }];
    });
  };

  const isAtLimit = () => items.length >= CART_LIMIT;

  const removeItem = (key) => {
    setItems(prev => prev.filter(p => (p.catalogue_number || p.id) !== key));
  };

  const updateQty = (key, qty) => {
    setItems(prev => prev.map(p =>
      (p.catalogue_number || p.id) === key ? { ...p, qty } : p
    ));
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(item =>
      (item.id === id || item.catalogue_number === id) ? { ...item, [field]: value } : item
    ));
  };

  const isInCart = (key) => items.some(p => (p.catalogue_number || p.id) === key);

  const itemCount = items.length;

  const clearCart = () => setItems([]);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, updateItem, isInCart, isAtLimit, itemCount, clearCart, cartMode, setCartMode, CART_LIMIT: 10 }}>
      {children}
    </CartContext.Provider>
  );
}

function AuthDialogProvider({ children }) {
  const { login } = useAuth();
  const [showLabAuth, setShowLabAuth] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [authMode, setAuthMode]       = useState('login');

  const openLabAuth  = (message = '', mode = 'login') => { setAuthMessage(message); setAuthMode(mode); setShowLabAuth(true); };
  const openDistAuth = (message = '') => openLabAuth(message);
  const closeAll     = () => { setShowLabAuth(false); setAuthMessage(''); setAuthMode('login'); };

  return (
    <AuthDialogContext.Provider value={{ openLabAuth, openDistAuth, closeAll }}>
      {children}
      <LabAuthDialog
        open={showLabAuth}
        onClose={closeAll}
        onSuccess={(userData) => {
          closeAll();
          const token = sessionStorage.getItem('lab_session_token');
          login(userData, token, 'lab');
        }}
        pendingMessage={authMessage}
        initialMode={authMode}
      />
    </AuthDialogContext.Provider>
  );
}

// ─── Global Floating Enquiry Bar — removed (EnquiryCart in BioMallHome handles this) ───

// ─── HomeRouter — shows marketing page when no query, search workspace when /?q= is set ─────────
function HomeRouter() {
  const [searchParams] = useSearchParams();
  const hasQuery = !!(searchParams.get('q') || searchParams.get('mode') || searchParams.get('open'));
  return hasQuery ? <BioMallHome /> : <HomepageV2 />;
}

function LoginPage() {
  const navigate = useNavigate();
  const { openLabAuth } = useAuthDialog();

  useEffect(() => {
    navigate('/', { replace: true });
    setTimeout(() => openLabAuth(), 100);
  }, []);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <QuoteBasketProvider>
          <AuthDialogProvider>
            <Toaster position="top-right" richColors />
            <Suspense fallback={<PageSkeleton />}>
            <Routes>
        <Route path="/" element={<HomeRouter />} />
        <Route path="/for-distributors" element={<ForDistributors />} />
        <Route path="/for-manufacturers" element={<ForManufacturers />} />
        <Route path="/contact"          element={<ContactUs />} />
        <Route path="/aboutus"          element={<AboutUs />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-sales" element={<TermsOfSales />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route path="/adminf123" element={<AdminLogin />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="/admin/brands" element={<RequireAdmin><AdminBrands /></RequireAdmin>} />
        <Route path="/admin/enquiries" element={<Navigate to="/admin/pi" replace />} />
        <Route path="/admin/distributors" element={<RequireAdmin><AdminDistributors /></RequireAdmin>} />
        <Route path="/admin/subscriptions" element={<RequireAdmin><AdminSubscriptions /></RequireAdmin>} />
        <Route path="/admin/ads" element={<RequireAdmin><AdminAds /></RequireAdmin>} />
        <Route path="/admin/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
        <Route path="/admin/plans"     element={<RequireAdmin><AdminPlans /></RequireAdmin>} />
        <Route path="/admin/products"  element={<RequireAdmin><AdminProducts /></RequireAdmin>} />
        <Route path="/admin/sponsored-products" element={<RequireAdmin><AdminSponsoredProducts /></RequireAdmin>} />
        <Route path="/admin/pi" element={<RequireAdmin><AdminPI /></RequireAdmin>} />
        <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
        <Route path="/admin/page-content" element={<RequireAdmin><AdminPageContent /></RequireAdmin>} />
        <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
        <Route path="/admin/seo"      element={<RequireAdmin><AdminSEO /></RequireAdmin>} />
        <Route path="/admin/staff" element={<RequireAdmin><AdminStaff /></RequireAdmin>} />
        <Route path="/admin/outreach" element={<RequireAdmin><AdminOutreach /></RequireAdmin>} />
        <Route path="/admin/offline-orders" element={<RequireAdmin><AdminOfflineOrders /></RequireAdmin>} />
        <Route path="/admin/lead-delivery" element={<RequireAdmin><AdminLeadDelivery /></RequireAdmin>} />
        <Route path="/admin/searches"      element={<RequireAdmin><AdminSearches /></RequireAdmin>} />
        <Route path="/distributors" element={<DistributorsList />} />
        <Route path="/distributors/:slug" element={<DistributorPage />} />
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/product/:productId" element={<ProductPage />} />
        <Route path="/brands/:slug" element={<BrandPage />} />
        {/* Redirect old biomall.in URL patterns → current brand page */}
        <Route path="/brands/:slug/*"      element={<RedirectToBrand />} />
        <Route path="/brand/:slug"         element={<RedirectToBrand />} />
        <Route path="/brand/:slug/*"       element={<RedirectToBrand />} />
        <Route path="/Brand-Store/:slug"   element={<RedirectToBrand />} />
        <Route path="/Brand-Store/:slug/*" element={<RedirectToBrand />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/category/:slug/*" element={<RedirectToCategory />} />
        <Route path="/offers" element={<Navigate to="/" replace />} />
        <Route path="/pricing" element={<PricingPage />} />
        {/* /me route removed — old profile page no longer in use */}
        <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="/s/:slug" element={<Microsite />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/admin/blog" element={<RequireAdmin><AdminBlog /></RequireAdmin>} />
        <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </AuthDialogProvider>
          </QuoteBasketProvider>
        </CartProvider>
      </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const WrappedApp = () => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <App />
  </GoogleOAuthProvider>
);

export default WrappedApp;